import type { ExtensionHandler } from '@atlaskit/editor-common/extensions/extension-handler';
import { getExtensionRenderer } from '@atlaskit/editor-common/utils';

import type { ADFRendererProps } from '@confluence/adf-renderer/entry-points/ADFRendererComponent.ts';
import { EXTENSION_TYPE } from '@confluence/fabric-extension-lib/entry-points/extensionConstants.ts';
import { expValEquals } from '@confluence/feature-experiments';

/** Combines cached Forge output with the fallback appropriate to this rendering phase. */
export function withPreloadedStaticMacroHandlers(
	fallbackHandlers: NonNullable<ADFRendererProps['extensionHandlers']>,
	preloadedHandlers: ADFRendererProps['extensionHandlers'],
): NonNullable<ADFRendererProps['extensionHandlers']> {
	if (!preloadedHandlers) {
		return fallbackHandlers;
	}

	const staticHandler = preloadedHandlers['com.atlassian.ecosystem'];
	if (!staticHandler) {
		return { ...fallbackHandlers, ...preloadedHandlers };
	}

	const renderStatic = getExtensionRenderer(staticHandler);
	const fallback = fallbackHandlers[EXTENSION_TYPE.MACRO_CORE];
	const renderFallback = fallback ? getExtensionRenderer(fallback) : undefined;

	return {
		...fallbackHandlers,
		...preloadedHandlers,
		[EXTENSION_TYPE.MACRO_CORE]: (...args: Parameters<ExtensionHandler>) => {
			const [extension] = args;
			if (
				extension.parameters?.macroMetadata?.macroId?.value &&
				expValEquals('confluence_connect_on_forge_ssr', 'isEnabled', true)
			) {
				// The Forge static handler also understands Connect-shaped nodes and looks up their
				// macro instance ID in the SSR cache. It never loads a client extension manifest.
				const staticOutput = renderStatic(...args);
				if (staticOutput) {
					return staticOutput;
				}
			}

			// A cache miss does not identify Connect: interactive apps and preload misses still use
			// the current SSR fallback, then release to the client provider at manifest readiness.
			// TODO(CCP-10331): use authoritative rendererKind when the backend provides it.
			return renderFallback ? renderFallback(...args) : null;
		},
	};
}
